import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import {
  breakpoints,
  fontStyle,
  palette,
  spacing,
  Button,
  Modal,
  Typography,
  fontSize,
} from '@playdapp/ui';
import Link from 'next/link';
import { Tooltip, Input } from '@chakra-ui/react';
import { capitalize } from 'lodash';
import { useMedia } from 'react-use';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import { editNickName } from 'api/user';
import { stakingMessage } from 'data/word';
import useSolana from 'hooks/useSolana';
import useOpenControl from 'hooks/useOpenControl';
import useToastMessage from 'hooks/useToastMessage';
import { MEMBERS_URL } from 'data/constants';
import { setPageNetwork } from 'store/page';
import { resetWalletInfo } from 'store/wallet';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  ConnectProps,
  resetUserInfo,
  setAlias,
  setProfileTokenId,
} from 'store/user';
import { removeLoginInfo } from 'lib/auth';
import { chainIdToMainNetworkName } from 'lib/network';
import {
  checkMobileDevice,
  convertAddress,
  getBreakpointQuery,
} from 'lib/util';

import Icon from '@/components/Icon';
import Image from '@/components/Image';
import Dimmer from '@/components/Dimmer';
import IFrame from '@/components/IFrame';
import SpinnerLayer from '@/components/SpinnerLayer';
import MembersSignInModal from '@/components/Modal/MembersSignInModal';

type FormData = {
  [key: string]: string;
  account: string;
};

const SideInfoBlock = styled.div`
  padding: ${spacing.m};
  margin-bottom: ${spacing.m};
  background-color: white;
  border-radius: ${spacing.xl};

  ${breakpoints.down('lg')} {
    display: flex;
    padding: ${spacing.m};
    gap: ${spacing.m};
  }

  ${breakpoints.down('md')} {
    display: flex;
    padding: ${spacing.m};
  }

  @media screen and (max-width: 540px) {
    display: block;
    gap: ${spacing.xxs};
    border-radius: 0;
  }
`;

const ProfileImage = styled.div<{ isStake: boolean }>`
  position: relative;
  width: ${({ theme }) => theme.rem(116)};
  height: ${({ theme }) => theme.rem(116)};
  margin: auto;
  border: 3px solid ${({ isStake }) => (!isStake ? palette.gray300 : '#74d1fc')};
  border-radius: 12px;

  img {
    border-radius: 9px;
    object-fit: cover;
  }

  ${breakpoints.down('lg')} {
    min-width: ${({ theme }) => theme.rem(214)};
    min-height: ${({ theme }) => theme.rem(214)};
    margin: 0 auto ${spacing.l} auto;
  }

  ${breakpoints.down('md')} {
    display: flex;
    min-width: ${({ theme }) => theme.rem(116)};
    min-height: ${({ theme }) => theme.rem(116)};
    margin: 0 auto ${spacing.m} auto;
  }

  @media screen and (max-width: 540px) {
    display: block;
    min-width: ${({ theme }) => theme.rem(84)};
    min-height: ${({ theme }) => theme.rem(84)};
  }
`;

const NonStakeButton = styled(Button)`
  position: absolute;
  max-width: ${({ theme }) => theme.rem(68)};
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${spacing.xxs} 0;
  margin: auto auto ${spacing.xs} auto;
  border-radius: 6px;
  ${fontStyle('b5')};

  ${breakpoints.down('lg')} {
    min-width: ${({ theme }) => theme.rem(100)};
    min-height: ${({ theme }) => theme.rem(32)};
    margin-bottom: ${spacing.s};
  }

  ${breakpoints.down('md')} {
    min-width: ${({ theme }) => theme.rem(68)};
    min-height: ${({ theme }) => theme.rem(24)};
    margin-bottom: ${spacing.xs};
    ${fontStyle('b6')};
  }
`;

const ImageSetting = styled.div`
  position: absolute;
  width: ${spacing.xl};
  height: ${spacing.xl};
  bottom: ${spacing.xs};
  right: ${spacing.xs};
  margin: 0;
  cursor: pointer;
`;

const WalletAddressBlock = styled.div`
  > p {
    width: 100%;
    word-break: break-all;
  }

  ${breakpoints.down('lg')} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding-bottom: ${spacing.m};
    margin-bottom: ${spacing.s};
    border-bottom: 1px solid ${palette.gray300};
  }

  ${breakpoints.down('md')} {
    ${fontStyle('p5')};
  }

  @media screen and (max-width: 540px) {
    display: block;
  }
`;

const ProfileInfoBlock = styled.div`
  width: ${({ theme }) => theme.rem(280)};
  margin-top: ${spacing.m};
  padding: ${spacing.s};
  border-radius: ${spacing.m};
  background-color: ${palette.gray200};

  ${breakpoints.down('lg')} {
    width: 100%;
    margin-top: 0;
  }
`;

const VerifyEmail = styled(Button)`
  width: 100%;
  ${fontStyle('b4')};
  border-radius: 6px;
`;

const ProfileEmailBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xxs};
  ${fontStyle('b3')};

  p {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  ${breakpoints.down('md')} {
    font-size: 14px;
    padding: 0;
  }
`;

const WalletInfoBlock = styled.div`
  height: 100%;
  margin-top: ${spacing.s};
  padding: ${spacing.s};
  border-radius: ${spacing.xs};
  background-color: ${palette.white};
  text-align: left;

  ${breakpoints.down('lg')} {
    height: auto;
    margin-top: ${spacing.s};
  }

  ${breakpoints.down('md')} {
    p {
      ${fontStyle('p3')};
    }
  }
`;

const WalletCurrencyBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.m};
`;

const NetworkBlock = styled.div`
  height: ${spacing.xl};
  display: flex;
  align-items: center;
  gap: ${spacing.xxs};

  div {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing.xxs};
  }
`;

const SettingBlock = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${spacing.xl};
  gap: ${spacing.xxs};
  border-radius: ${spacing.xs};
  cursor: pointer;
  padding: ${spacing.xs};
`;

const CurrencyTextBlock = styled.div`
  height: 100%;
  padding: 0 ${spacing.xxs};
  background-color: ${palette.gray200};
  border-radius: ${spacing.xxs};
`;

const WalletAddressText = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding-bottom: ${spacing.s};
  margin: ${spacing.xs} 0 ${spacing.s} 0;
  border-bottom: 1px solid ${palette.gray300};
  gap: ${spacing.xs};
  color: ${palette.dgray100};

  span {
    cursor: pointer;
  }

  ${breakpoints.down('lg')} {
    border-bottom: 0;
    padding: 0;
    margin: 0;
    justify-content: flex-end;
  }

  @media screen and (max-width: 540px) {
    justify-content: flex-start;
  }
`;

const UserLogout = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: ${spacing.s};
  margin: auto;
  cursor: pointer;

  ${breakpoints.down('md')} {
    padding: 0;
  }
`;

const WalletButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  cursor: pointer;

  & > p {
    ${fontStyle('p3')}
    color: ${palette.dgray100};
    text-decoration: underline;

    ${breakpoints.down('sm')} {
      ${fontStyle('p5')}
    }
  }

  & > span {
    display: none;
    margin-right: ${spacing.xs} !important;
  }

  ${breakpoints.up('md')} {
    & > span {
      display: none;
    }
  }
`;

const ConnectContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  max-height: ${({ theme }) => theme.rem(600)};
  border-bottom: 1px solid ${palette.gray300};
  overflow-y: auto;
  text-align: center;

  ${breakpoints.down('lg')} {
    max-height: ${({ theme }) => theme.rem(380)};
  }

  ${breakpoints.down('md')} {
    max-height: ${({ theme }) => theme.rem(330)};
    text-align: left;
    -ms-overflow-style: none;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ConnectTitle = styled.div`
  ${fontStyle('h4')};
  margin-bottom: ${spacing.s};

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h5};
    font-weight: 800;
    margin-bottom: ${spacing.xl};
    text-align: left;
  }
`;

const ConnectCard = styled.div`
  height: 100%;
  text-align: left;

  & > div {
    padding: ${spacing.m};
    border: 1px solid ${palette.gray300};
    border-bottom: none;
  }
`;

const CardUser = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};

  span {
    cursor: pointer;
  }
`;

const AddressNickName = styled.div<{ isCurrentAddress: boolean }>`
  ${fontStyle('b3')};
  margin-right: ${spacing.s};
  color: ${({ isCurrentAddress }) => isCurrentAddress && palette.primary700};
`;

const EditInput = styled(Input)<{ isInvalid: boolean }>`
  height: 38px;
  padding: 0 ${spacing.s};
  background-color: ${palette.white};
  border-radius: ${spacing.xs};
  border: 1px solid ${palette.gray600};
  box-shadow: none;

  ${({ isInvalid }) =>
    isInvalid
      ? `
    border: 1px solid #EF586E;
    box-shadow: none !important;

    &:focus {
      outline: none;
      box-shadow: none;
      border: 1px solid #EF586E;
    }
  `
      : ``}

  &.connect-address {
    max-width: ${({ theme }) => theme.rem(324)};
  }

  ${breakpoints.down('md')} {
    height: 34px;
    font-size: ${fontSize.b3};
  }
`;

const CheckButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.xxxl};
  height: ${spacing.xxxl};
  padding: ${spacing.s};
  border-radius: 8px;
`;

const CancelButton = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.xxxl};
  height: ${spacing.xxxl};
  top: 0;
  padding: ${spacing.s};
  outline: 0;
  border: 1px solid ${palette.gray900};
  border-radius: 8px;

  & > span {
    margin: 0;
  }

  :hover {
    border: 1px solid ${palette.gray900};
  }
`;

const Label = styled.label`
  font-size: ${fontSize.p4};
  color: ${palette.gray900};
`;

const ErrorMessage = styled(Label)`
  color: #ef586e;
  font-size: ${fontSize.p4};
`;

const CardWallet = styled.div<{ isCurrentAddress: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  height: ${spacing.xxl};
  margin-top: ${spacing.s};
  border-radius: ${spacing.xs};
  font-size: 14px;
  background-color: ${({ isCurrentAddress }) =>
    isCurrentAddress ? palette.primary100 : palette.gray200};
  text-align: center;

  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const ConnectCancel = styled(Button)`
  width: 100%;
  border: 1px solid ${palette.gray900};
  border-radius: ${spacing.xs};
  margin: ${spacing.xxl} auto 0 auto;
  color: ${palette.dgray300};
  ${fontStyle('p3')};

  :hover {
    ${fontStyle('b3')};
  }
`;

const AccountCopyButton = React.forwardRef<
  HTMLButtonElement,
  { account: string; handleCopy: () => void }
>(({ account, handleCopy }, ref) => (
  <CopyToClipboard text={account} onCopy={handleCopy}>
    <WalletButton ref={ref}>
      <p>{convertAddress(account, 5, 38)}</p>
      <Icon name="copy" size={14} color={palette.dgray100} />
    </WalletButton>
  </CopyToClipboard>
));

type WalletProps = {
  address: string;
  alias?: string;
  originalAlias?: string;
};

const IFrameWrapper = styled.div<{ isVisible: boolean }>`
  position: fixed;
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  padding-top: 64px;
  z-index: 9;

  iframe {
    width: 100%;
    height: 100%;
  }
`;

const MyProfile = () => {
  const [stake, setStake] = useState(false);
  const [isProfileOpen, handleProfileOpen] = useState(false);
  const [isIframeOpen, setIsIframeOpen] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  const [copied, setCopied] = useState(false);
  const [connectAccount, setConnectAccount] = useState('');
  const [connectList, setConnectList] = useState<ConnectProps[]>([]);
  const [isGuideOpened, setIsGuideOpened] = useState(false);

  const [currentAccount, setCurrentAccount] = useState<WalletProps>();
  const [myAccounts, setMyAccounts] = useState<WalletProps[]>([]);

  const [isConnectModal, setIsConnectModal] = useOpenControl();

  const isMdDownScreen = useMedia(getBreakpointQuery(breakpoints.down('md')));

  const router = useRouter();
  const dispatch = useAppDispatch();
  const { deactivate } = useWeb3React<Web3Provider>();
  const { disconnect } = useSolana();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { account, activeChainId, provider } = useAppSelector(
    ({ wallet }) => wallet,
  );
  const { alias, email, connected, profileTokenId, membersMikeyGrade } =
    useAppSelector(({ user }) => user);

  const networkName = useMemo(
    () => (activeChainId ? chainIdToMainNetworkName[activeChainId] : 'polygon'),
    [activeChainId],
  );

  const { handleUpdateToast } = useToastMessage({
    position: 'top',
    duration: 3500,
    id: 'convertessage',
  });

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const onSubmitInfo = (address: string) => async () => {
    const data = watch();
    const value = data[address];

    const response = await editNickName({
      account: address,
      alias: value,
    });

    if (response.status === 200 && response.data) {
      if (address === account) {
        dispatch(setAlias(value));
      }

      setConnectList(
        connectList.map((item) => ({
          ...item,
          alias: item.address === address ? value : item.alias,
        })),
      );
      setConnectAccount('');
    }
  };

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      try {
        if (e.origin === MEMBERS_URL) {
          const { action } = e.data;

          if (action === 'get_info' && isIframeOpen) {
            if (account === e.data.account) {
              const { stakedTokenId } = e.data;
              setStake(true);
              dispatch(setProfileTokenId(stakedTokenId));
            }
          }

          if (action === 'finish_approval' && isIframeOpen) {
            handleUpdateToast(
              <span>{stakingMessage.approved}</span>,
              'success',
              true,
            );
          }

          if (action === 'finish_unstaking' && isIframeOpen) {
            handleUpdateToast(
              <span>{stakingMessage.unstaking}</span>,
              'success',
              true,
            );
          }

          if (action === 'finish_staking' && isIframeOpen) {
            handleUpdateToast(
              <span>{stakingMessage.staking}</span>,
              'success',
              true,
            );
          }

          if (action === 'update_info' && isIframeOpen) {
            if (
              e.data.account &&
              account !== e.data.account &&
              !isGuideOpened
            ) {
              setIsGuideOpened(true);
              setIsIframeOpen(false);
              window.open(
                `${MEMBERS_URL}/connect?account=${account}`,
                '_blank',
              );
            }
          }
        }
      } catch (err) {
        handleUpdateToast(
          <span>Unknown error. Please try again.</span>,
          'error',
          true,
        );
      }
    },
    [dispatch, account, isIframeOpen, isGuideOpened, handleUpdateToast],
  );

  const handleIframeLoading = () => {
    setIsIframeLoading(false);
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        { action: 'info' },
        {
          targetOrigin: MEMBERS_URL,
        },
      );
    }
  };

  const handleProfile = () => {
    if (checkMobileDevice()) {
      window.open(`${MEMBERS_URL}/my-page/items`, '_blank');
      return;
    }

    handleProfileOpen(true);
  };

  const handleProfileReset = () => {
    reset({
      formState: '',
    });
    setCurrentAccount(undefined);
  };

  const toggleConnectModal =
    (isOpen: boolean, address: string, alias: string) => () => {
      setIsConnectModal(isOpen);

      if (!isOpen) {
        setConnectAccount('');
        setValue(address, alias);
        handleProfileReset();
      }
    };

  const handleConnectNickName =
    (address: string, callback?: () => void) => () => {
      setConnectAccount(address);
      if (callback) {
        callback();
      }

      if (!isConnectModal) {
        handleProfileReset();
      }
    };

  const handleCancelEdit = (address: string, userAlias: string) => () => {
    setConnectAccount('');
    setValue(address, userAlias);
    handleProfileReset();
  };

  const handleLogout = () => {
    removeLoginInfo();
    dispatch(resetWalletInfo());
    dispatch(resetUserInfo());
    dispatch(setPageNetwork(null));
    deactivate();
    disconnect();
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  useEffect(() => {
    setConnectList(
      connected?.map((item) => ({
        ...item,
        originalAlias: item.alias,
        alias: item.alias,
      })),
    );
  }, [connected]);

  useEffect(() => {
    const nextCurrentAccount = connectList?.find(
      (item) => item.address === account,
    );
    const nextMyAccounts = connectList?.filter(
      (item) => item.address !== account,
    );
    setCurrentAccount(nextCurrentAccount);
    setMyAccounts(nextMyAccounts);
  }, [connectList, currentAccount]);

  const handleContinue = () => {
    setIsIframeOpen(true);
    setIsIframeLoading(true);
    setIsGuideOpened(false);
    handleProfileOpen(false);
    router.push('/my-page/items?type=members');
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    router.events.on('routeChangeComplete', (e) => {
      if (e !== '/my-page/items?type=members') {
        setIsIframeOpen(false);
        setIsIframeLoading(true);
        setIsGuideOpened(false);
      }
    });
  }, [router, isIframeLoading, isIframeOpen]);

  useEffect(() => {
    const bodyElement = document.querySelector('body');

    if (isIframeOpen) {
      if (bodyElement && iframeRef.current && !isIframeLoading) {
        bodyElement.style.overflow = 'hidden';
      }
    } else {
      bodyElement?.removeAttribute('style');
    }

    return () => {
      bodyElement?.removeAttribute('style');
    };
  }, [isIframeOpen, isIframeLoading]);

  return (
    <>
      <SideInfoBlock>
        {networkName === 'polygon' ? (
          <div>
            {profileTokenId && membersMikeyGrade ? (
              <ProfileImage isStake={stake}>
                <Image
                  src={`${process.env.NEXT_PUBLIC_MEMBERS_IMAGE_URL}/${membersMikeyGrade}/gif/${profileTokenId}.gif`}
                  layout="fill"
                  objectFit="cover"
                  width={32}
                  height={32}
                />
                <ImageSetting onClick={handleProfile}>
                  <Icon name="setting-green" size={24} alt="setting-primary" />
                </ImageSetting>
              </ProfileImage>
            ) : (
              <ProfileImage isStake={stake}>
                <Image
                  prefix="s3"
                  src="/mypage/non-stake.png"
                  layout="fill"
                  objectFit="cover"
                  alt="info-img"
                />
                <NonStakeButton
                  size="xs"
                  color="primary"
                  onClick={handleProfile}
                >
                  Stake
                </NonStakeButton>
              </ProfileImage>
            )}
          </div>
        ) : (
          <ProfileImage isStake={stake}>
            <Image
              prefix="s3"
              src="/mypage/empty-stake.png"
              layout="fill"
              objectFit="cover"
              alt="info-img"
            />
          </ProfileImage>
        )}

        <ProfileInfoBlock>
          {!email ? (
            <Link href="/email/verification" passHref>
              <VerifyEmail>Verify Email</VerifyEmail>
            </Link>
          ) : (
            <ProfileEmailBlock>
              <p>{email}</p>
              <Icon name="bottle-check" size={16} />
            </ProfileEmailBlock>
          )}

          <WalletInfoBlock>
            <WalletCurrencyBlock>
              <NetworkBlock>
                {provider && <Icon name={`${provider}-square`} size={24} />}
                <CurrencyTextBlock>
                  <Icon name="dot-green" size={4} />
                  <Typography type="p5">{capitalize(networkName)}</Typography>
                </CurrencyTextBlock>
              </NetworkBlock>

              <SettingBlock
                variant="ghost"
                color="secondary"
                onClick={toggleConnectModal(
                  true,
                  currentAccount?.address as string,
                  currentAccount?.alias as string,
                )}
              >
                <Icon name="setting" size={12} />
                <Typography type="p5" color="dgray600">
                  Setting
                </Typography>
              </SettingBlock>
            </WalletCurrencyBlock>

            <WalletAddressBlock>
              <p>{alias}</p>

              <WalletAddressText>
                {account && (
                  <Tooltip label="Copied!" isOpen={copied} placement="bottom">
                    <AccountCopyButton
                      account={account}
                      handleCopy={handleCopy}
                    />
                  </Tooltip>
                )}
              </WalletAddressText>
            </WalletAddressBlock>

            <UserLogout
              variant="ghost"
              color="secondary"
              onClick={handleLogout}
            >
              <Icon name="log-switch" size={14} alt="login-switch" />
              <Typography type="p5" color="dgray600">
                Sign out
              </Typography>
            </UserLogout>
          </WalletInfoBlock>
        </ProfileInfoBlock>

        <Modal
          isOpen={isConnectModal}
          handleOpen={toggleConnectModal(
            false,
            currentAccount?.address as string,
            currentAccount?.alias as string,
          )}
          shouldCloseOnOverlayClick
          width={isMdDownScreen ? '100%' : '520px'}
        >
          <ConnectContainer>
            <ConnectTitle>Connected Addresses</ConnectTitle>

            {currentAccount && (
              <ConnectCard>
                <div>
                  {account !== connectAccount ? (
                    <CardUser>
                      <AddressNickName
                        isCurrentAddress={account === currentAccount.address}
                      >
                        {currentAccount.alias
                          ? currentAccount.alias
                          : 'Please Setting NickaName'}
                      </AddressNickName>
                      <Icon
                        name="edit"
                        alt="edit"
                        size={20}
                        onClick={handleConnectNickName(currentAccount.address)}
                      />
                    </CardUser>
                  ) : (
                    <>
                      <CardUser className="connect-box">
                        <EditInput
                          className="connect-address"
                          type="text"
                          isRequired
                          isInvalid={!!errors[currentAccount.address]}
                          placeholder="Edit your Nickname"
                          {...register(currentAccount.address, {
                            required: 'Please Enter Nickname',
                            pattern: {
                              value: /^[a-zA-Z0-9]{2,20}$/,
                              message: 'This nickname is not valid',
                            },
                          })}
                          defaultValue={currentAccount.alias}
                        />

                        <CancelButton
                          variant="outline"
                          color="secondary"
                          iconOnly
                          onClick={handleCancelEdit(
                            currentAccount.address,
                            currentAccount.alias as string,
                          )}
                        >
                          <Icon name="cancel-mark" size={12} />
                        </CancelButton>

                        <CheckButton
                          variant="solid"
                          color="primary"
                          iconOnly
                          disabled={!!errors[currentAccount.address]}
                          onClick={handleConnectNickName(
                            currentAccount.address,
                            onSubmitInfo(currentAccount.address),
                          )}
                        >
                          <Icon name="check-mark-white" size={12} />
                        </CheckButton>
                      </CardUser>
                      {errors[currentAccount.address] && (
                        <ErrorMessage>
                          {errors[currentAccount.address]?.message}
                        </ErrorMessage>
                      )}
                    </>
                  )}
                  <CardWallet
                    isCurrentAddress={account === currentAccount.address}
                  >
                    <p>{currentAccount.address}</p>
                  </CardWallet>
                </div>
              </ConnectCard>
            )}

            {myAccounts?.map((info, index) => (
              <ConnectCard key={index}>
                <div>
                  {info.address !== connectAccount ? (
                    <CardUser>
                      <AddressNickName
                        isCurrentAddress={account === info.address}
                      >
                        {info.alias ? info.alias : 'Please Setting NickaName'}
                      </AddressNickName>
                      <Icon
                        name="edit"
                        alt="edit"
                        size={20}
                        onClick={handleConnectNickName(info.address)}
                      />
                    </CardUser>
                  ) : (
                    <>
                      <CardUser className="connect-box">
                        <EditInput
                          className="connect-address"
                          type="text"
                          isRequired
                          isInvalid={!!errors[info.address]}
                          placeholder="Edit your Nickname"
                          {...register(info.address, {
                            required: 'Please Enter Nickname',
                            pattern: {
                              value: /^[a-zA-Z0-9]{2,20}$/,
                              message: 'This nickname is not valid',
                            },
                          })}
                          defaultValue={info.alias}
                        />
                        <CancelButton
                          variant="outline"
                          color="secondary"
                          iconOnly
                          onClick={handleCancelEdit(
                            info.address,
                            info.alias as string,
                          )}
                        >
                          <Icon name="cancel-mark" size={12} />
                        </CancelButton>

                        <CheckButton
                          variant="solid"
                          color="primary"
                          iconOnly
                          disabled={!!errors[info.address]}
                          onClick={handleConnectNickName(
                            info.address,
                            onSubmitInfo(info.address),
                          )}
                        >
                          <Icon name="check-mark-white" size={12} />
                        </CheckButton>
                      </CardUser>
                      {errors[info.address] && (
                        <ErrorMessage>
                          {errors[info.address]?.message}
                        </ErrorMessage>
                      )}
                    </>
                  )}
                  <CardWallet isCurrentAddress={account === info.address}>
                    <p>{info.address}</p>
                  </CardWallet>
                </div>
              </ConnectCard>
            ))}
          </ConnectContainer>
          <ConnectCancel
            variant="outline"
            color="secondary"
            onClick={toggleConnectModal(
              false,
              currentAccount?.address as string,
              currentAccount?.alias as string,
            )}
          >
            OK
          </ConnectCancel>
        </Modal>
      </SideInfoBlock>

      {isIframeOpen && (
        <IFrameWrapper isVisible>
          {isIframeLoading && (
            <Dimmer isOpen={isIframeLoading}>
              <SpinnerLayer />
            </Dimmer>
          )}
          <IFrame
            ref={iframeRef}
            src={`${MEMBERS_URL}/my-page-embed?type=market`}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="autoplay"
            onLoad={handleIframeLoading}
            loading="lazy"
          />
        </IFrameWrapper>
      )}

      <MembersSignInModal
        isOpen={isProfileOpen}
        handleOpen={handleProfileOpen}
        handleContinue={handleContinue}
      />
    </>
  );
};

export default MyProfile;
